import { useMemo, useState } from "react";
import { calculateRiskPlan } from "../lib/analysis/risk.js";
import { formatChf, formatNumber, formatUsd } from "../lib/utils/formatters.js";
import Panel from "./Panel.jsx";

function Field({ label, value, onChange, suffix, min = 0, step = "any" }) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-muted">{label}</span>
      <div className="mt-1 flex items-center rounded-md border border-white/10 bg-ink/40 px-3">
        <input
          className="min-w-0 flex-1 bg-transparent py-2 text-sm text-white outline-none"
          type="number"
          min={min}
          step={step}
          value={value}
          onChange={(event) => onChange(Number(event.target.value))}
        />
        {suffix ? <span className="text-xs text-slate-400">{suffix}</span> : null}
      </div>
    </label>
  );
}

export default function RiskCalculator({ currentPrice = 0 }) {
  const [capitalChf, setCapitalChf] = useState(10000);
  const [riskPercent, setRiskPercent] = useState(1);
  const [entryPrice, setEntryPrice] = useState(currentPrice || 4700);
  const [stopLossPercent, setStopLossPercent] = useState(1.5);
  const [rewardRiskRatio, setRewardRiskRatio] = useState(2);

  const plan = useMemo(
    () =>
      calculateRiskPlan({
        capitalChf,
        riskPercent,
        entryPrice: entryPrice || currentPrice,
        stopLossPercent,
        rewardRiskRatio,
      }),
    [capitalChf, riskPercent, entryPrice, currentPrice, stopLossPercent, rewardRiskRatio],
  );

  return (
    <Panel title="Калькулятор риска" icon="shield">
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Капитал" value={capitalChf} onChange={setCapitalChf} suffix="CHF" step="100" />
        <Field label="Риск на сделку" value={riskPercent} onChange={setRiskPercent} suffix="%" step="0.1" />
        <Field label="Цена входа" value={entryPrice} onChange={setEntryPrice} suffix="USD" step="1" />
        <Field label="Стоп-лосс" value={stopLossPercent} onChange={setStopLossPercent} suffix="%" step="0.1" />
        <Field label="Риск/прибыль" value={rewardRiskRatio} onChange={setRewardRiskRatio} suffix="R" step="0.1" />
      </div>

      {plan.valid ? (
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          <Output label="Сумма риска" value={formatChf(plan.riskAmountChf)} />
          <Output label="Цена стоп-лосса" value={formatUsd(plan.stopLossPrice)} />
          <Output label="Цена тейк-профита" value={formatUsd(plan.takeProfitPrice)} />
          <Output label="Размер позиции" value={`${formatNumber(plan.suggestedPositionOz, 3)} oz`} />
        </div>
      ) : (
        <p className="mt-4 rounded-md border border-rose/30 bg-rose/10 p-3 text-sm text-rose">{plan.warning}</p>
      )}

      {plan.warnings?.length ? (
        <div className="mt-4 space-y-2">
          {plan.warnings.map((warning) => (
            <p key={warning} className="rounded-md border border-gold/30 bg-gold/10 p-3 text-sm text-goldSoft">
              {warning}
            </p>
          ))}
        </div>
      ) : null}
    </Panel>
  );
}

function Output({ label, value }) {
  return (
    <div className="rounded-md border border-white/10 bg-white/[0.03] p-3">
      <div className="text-xs text-muted">{label}</div>
      <div className="mt-1 text-sm font-semibold text-white">{value}</div>
    </div>
  );
}
